import type {
  DashboardTableDateFilter,
  DashboardTableQuery,
  DashboardTableSortDirection,
  DashboardTableSortState,
} from '@fluenceur/types';
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

import {
  getDashboardTableQueryParamNames,
  getNextDashboardTableSortState,
} from './dashboard-table-utils';

const SORT_DIRECTION_OPTIONS = ['asc', 'desc'] as const;

interface UseDashboardTableQueryStateOptions<SortBy extends string, DateFilterType extends string> {
  namespace: string;
  sortByOptions: readonly [SortBy, ...SortBy[]];
  defaultSort: DashboardTableSortState<SortBy>;
  defaultPage?: number;
  defaultPerPage?: number;
  dateFilterTypeOptions?: readonly [DateFilterType, ...DateFilterType[]];
  defaultDateFilterType?: DateFilterType;
}

interface UseDashboardTableQueryStateResult<SortBy extends string, DateFilterType extends string> {
  query: DashboardTableQuery<SortBy, DateFilterType>;
  sort: DashboardTableSortState<SortBy>;
  dateFilter: DashboardTableDateFilter<DateFilterType>;
  paramNames: ReturnType<typeof getDashboardTableQueryParamNames>;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSort: (sort: DashboardTableSortState<SortBy>) => void;
  toggleSort: (column: SortBy, defaultDirection?: DashboardTableSortDirection) => void;
  setDateFilter: (dateFilter: DashboardTableDateFilter<DateFilterType>) => void;
  clearDateFilter: () => void;
}

const buildDateFilterTypeParser = <DateFilterType extends string>(
  dateFilterTypeOptions?: readonly [DateFilterType, ...DateFilterType[]],
  defaultDateFilterType?: DateFilterType
) => {
  if (!dateFilterTypeOptions) {
    return parseAsString;
  }

  const parser = parseAsStringLiteral(dateFilterTypeOptions);
  return defaultDateFilterType ? parser.withDefault(defaultDateFilterType) : parser;
};

export const useDashboardTableQueryState = <
  SortBy extends string,
  DateFilterType extends string = string,
>({
  namespace,
  sortByOptions,
  defaultSort,
  defaultPage = 1,
  defaultPerPage = 20,
  dateFilterTypeOptions,
  defaultDateFilterType,
}: UseDashboardTableQueryStateOptions<SortBy, DateFilterType>): UseDashboardTableQueryStateResult<
  SortBy,
  DateFilterType
> => {
  const paramNames = useMemo(() => getDashboardTableQueryParamNames(namespace), [namespace]);
  const dateFilterTypeParser = useMemo(
    () => buildDateFilterTypeParser(dateFilterTypeOptions, defaultDateFilterType),
    [dateFilterTypeOptions, defaultDateFilterType]
  );

  const [page, setPageRaw] = useQueryState(
    paramNames.page,
    parseAsInteger.withDefault(defaultPage)
  );
  const [perPage, setPerPageRaw] = useQueryState(
    paramNames.perPage,
    parseAsInteger.withDefault(defaultPerPage)
  );
  const [sortBy, setSortBy] = useQueryState(
    paramNames.sortBy,
    parseAsStringLiteral(sortByOptions).withDefault(defaultSort.sortBy)
  );
  const [sortDirection, setSortDirection] = useQueryState(
    paramNames.sortDirection,
    parseAsStringLiteral(SORT_DIRECTION_OPTIONS).withDefault(defaultSort.sortDirection)
  );
  const [dateFilterType, setDateFilterType] = useQueryState(
    paramNames.dateFilterType,
    dateFilterTypeParser
  );
  const [startDate, setStartDate] = useQueryState(paramNames.startDate, parseAsString);
  const [endDate, setEndDate] = useQueryState(paramNames.endDate, parseAsString);

  const resetPage = useCallback(() => {
    void setPageRaw(defaultPage);
  }, [defaultPage, setPageRaw]);

  const setPage = useCallback(
    (nextPage: number) => {
      const clampedPage = Math.max(1, nextPage);
      void setPageRaw(clampedPage);
    },
    [setPageRaw]
  );

  const setPerPage = useCallback(
    (nextPerPage: number) => {
      const clampedPerPage = Math.max(1, nextPerPage);
      if (clampedPerPage !== perPage) {
        void setPerPageRaw(clampedPerPage);
        resetPage();
      }
    },
    [perPage, resetPage, setPerPageRaw]
  );

  const setSort = useCallback(
    (nextSort: DashboardTableSortState<SortBy>) => {
      if (nextSort.sortBy === sortBy && nextSort.sortDirection === sortDirection) {
        return;
      }

      void setSortBy(nextSort.sortBy);
      void setSortDirection(nextSort.sortDirection);
      resetPage();
    },
    [resetPage, setSortBy, setSortDirection, sortBy, sortDirection]
  );

  const toggleSort = useCallback(
    (column: SortBy, defaultDirection: DashboardTableSortDirection = 'desc') => {
      setSort(
        getNextDashboardTableSortState(
          { sortBy, sortDirection },
          column,
          defaultSort,
          defaultDirection
        )
      );
    },
    [defaultSort, setSort, sortBy, sortDirection]
  );

  const setDateFilter = useCallback(
    (nextDateFilter: DashboardTableDateFilter<DateFilterType>) => {
      const nextDateFilterType = nextDateFilter.dateFilterType ?? null;
      const nextStartDate = nextDateFilter.startDate ?? null;
      const nextEndDate = nextDateFilter.endDate ?? null;

      if (
        nextDateFilterType === dateFilterType &&
        nextStartDate === startDate &&
        nextEndDate === endDate
      ) {
        return;
      }

      void setDateFilterType(nextDateFilterType);
      void setStartDate(nextStartDate);
      void setEndDate(nextEndDate);
      resetPage();
    },
    [dateFilterType, endDate, resetPage, setDateFilterType, setEndDate, setStartDate, startDate]
  );

  const clearDateFilter = useCallback(() => {
    setDateFilter({});
  }, [setDateFilter]);

  const query = useMemo(
    () => ({
      page,
      perPage,
      sortBy,
      sortDirection,
      dateFilterType: (dateFilterType ?? undefined) as DateFilterType | undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
    }),
    [dateFilterType, endDate, page, perPage, sortBy, sortDirection, startDate]
  );
  const sort = useMemo(
    () => ({
      sortBy,
      sortDirection,
    }),
    [sortBy, sortDirection]
  );
  const dateFilter = useMemo(
    () => ({
      dateFilterType: (dateFilterType ?? undefined) as DateFilterType | undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
    }),
    [dateFilterType, endDate, startDate]
  );

  return {
    query,
    sort,
    dateFilter,
    paramNames,
    setPage,
    setPerPage,
    setSort,
    toggleSort,
    setDateFilter,
    clearDateFilter,
  };
};
